import { setScheduleCacheIdentity, advanceSession } from "@/lib/offline";
import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";

import { operatingProfileState } from "@/lib/operatingProfile";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauthenticated, object = authed
  const [loading, setLoading] = useState(true);
  const [profileRequestFailed, setProfileRequestFailed] = useState(false);
  const sessionGeneration = useRef(0);
  const profileRequestGeneration = useRef(0);

  const refresh = useCallback(async () => {
    const generation = sessionGeneration.current;
    const requestGeneration = ++profileRequestGeneration.current;
    try {
      const { data } = await api.get("/auth/me");
      if (generation !== sessionGeneration.current || requestGeneration !== profileRequestGeneration.current) return;
      setProfileRequestFailed(false);
      // Only apply if we're still in the initial checking state, so we
      // don't overwrite a freshly logged-in user via a race condition.
      setUser((prev) => {
        if (prev === null) { setScheduleCacheIdentity(data); return data; }
        // Refresh only additive profile metadata for the same active identity.
        if (prev && prev.id === data.id && prev.org_id === data.org_id) {
          return { ...prev, operating_context: data.operating_context };
        }
        return prev;
      });
    } catch (e) {
      if (generation !== sessionGeneration.current || requestGeneration !== profileRequestGeneration.current) return;
      setProfileRequestFailed(true);
      setUser((prev) => (prev === null ? false : prev));
    } finally {
      if (generation === sessionGeneration.current && requestGeneration === profileRequestGeneration.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.pathname.startsWith("/audit/")) { setUser(false); setLoading(false); return; }
    refresh();
  }, [refresh]);

  const beginVerification = () => {
    advanceSession();
    sessionGeneration.current += 1;
    localStorage.removeItem("fl_token");
    localStorage.removeItem("fl_refresh");
    setScheduleCacheIdentity(null);
    setUser(false);
    setLoading(false);
  };

  const login = async (email, password) => {
    advanceSession();
    try {
      const { data } = await api.post("/auth/login", { email, password }, { timeout: 20000, __flOnlineOnly: true });
      if (data.verification_required) {
        beginVerification();
        return { ok: false, verification: data };
      }
      if (data?.access_token) localStorage.setItem("fl_token", data.access_token);
      if (data?.refresh_token) localStorage.setItem("fl_refresh", data.refresh_token);
      localStorage.setItem("fl_last_activity", String(Date.now()));
      sessionGeneration.current += 1;
      setProfileRequestFailed(false);
      setLoading(false);
      setScheduleCacheIdentity(data);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      if (e.response?.status === 503) {
        return { ok: false, maintenance: true, error: formatApiError(e.response?.data?.detail) };
      }
      return { ok: false, error: e.code === "ECONNABORTED" || e.code === "ETIMEDOUT"
        ? "Sign-in timed out. Please try again in a moment."
        : !e.response ? "Unable to reach Fieldlink. Check your connection and try again."
        : formatApiError(e.response?.data?.detail) };
    }
  };

  const register = async (payload) => {
    advanceSession();
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data?.access_token) localStorage.setItem("fl_token", data.access_token);
      if (data?.refresh_token) localStorage.setItem("fl_refresh", data.refresh_token);
      localStorage.setItem("fl_last_activity", String(Date.now()));
      sessionGeneration.current += 1;
      setProfileRequestFailed(false);
      setLoading(false);
      setScheduleCacheIdentity(data);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const setSession = (data) => {
    advanceSession();
    if (data?.access_token) localStorage.setItem("fl_token", data.access_token);
    if (data?.refresh_token) localStorage.setItem("fl_refresh", data.refresh_token);
    localStorage.setItem("fl_last_activity", String(Date.now()));
    sessionGeneration.current += 1;
    setProfileRequestFailed(false);
    setLoading(false);
    setScheduleCacheIdentity(data);
    setUser(data);
  };

  const logout = async () => {
    advanceSession();
    sessionGeneration.current += 1;
    setProfileRequestFailed(false);
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("fl_token");
    localStorage.removeItem("fl_refresh");
    localStorage.removeItem("fl_last_activity");
    setScheduleCacheIdentity(null);
    setLoading(false);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, setSession, beginVerification, operatingProfile: operatingProfileState(user, loading, profileRequestFailed) }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
